import { useEffect, useMemo, useState } from 'react';

import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  useGetNoteTemplateRevisionByIdQuery,
  useGetNoteTemplateRevisionsQuery,
} from '~/store/services/notes/noteTemplateRevisions';

export const useLatestNoteTemplateRevisionsWithLegacyHandling = (
  selectedRevisionId: string,
  skip: boolean = false,
) => {
  const {
    currentUser: { providerTypeName },
  } = useGlobalContext();
  const {
    data: latestNoteTemplateRevisions,
    isLoading: isGetNoteTemplateRevisionsLoading,
  } = useGetNoteTemplateRevisionsQuery(
    {
      latestRevisionOnly: true,
    },
    { skip },
  );
  const {
    data: oldNoteTemplateRevision,
    isLoading: isGetNoteTemplateRevisionByIdLoading,
  } = useGetNoteTemplateRevisionByIdQuery(selectedRevisionId, {
    skip:
      skip ||
      !selectedRevisionId ||
      !latestNoteTemplateRevisions ||
      selectedRevisionId === NO_NOTE_TEMPLATE_OPTION.node ||
      latestNoteTemplateRevisions.some(
        (rev) => rev.templateRevisionId === selectedRevisionId,
      ),
  });
  const [selectedRevision, setSelectedRevision] = useState<
    NoteTemplateRevision | undefined
  >(undefined);

  // memoization necessary to avoid re-declarations which would cause re-runs of hooks that use these as deps
  const noteTemplateRevisions: NoteTemplateRevision[] = useMemo(() => {
    const result: NoteTemplateRevision[] = [];
    if (latestNoteTemplateRevisions) {
      result.push(
        ...latestNoteTemplateRevisions.filter(
          (rev) =>
            rev.template.providerTypes.includes(providerTypeName) ||
            rev.templateRevisionId === selectedRevisionId,
        ),
      );
    }
    if (oldNoteTemplateRevision) {
      result.push({
        ...oldNoteTemplateRevision,
        template: {
          ...oldNoteTemplateRevision.template,
          templateTitle: `${oldNoteTemplateRevision.template.templateTitle} (Old Version)`,
        },
      });
    }

    return result;
  }, [
    latestNoteTemplateRevisions,
    selectedRevisionId,
    oldNoteTemplateRevision,
  ]);

  useEffect(() => {
    setSelectedRevision(
      noteTemplateRevisions.find(
        (rev) => rev.templateRevisionId === selectedRevisionId,
      ),
    );
  }, [noteTemplateRevisions, selectedRevisionId]);

  return {
    noteTemplateRevisions,
    isRevisionDataLoading:
      isGetNoteTemplateRevisionsLoading || isGetNoteTemplateRevisionByIdLoading,
    selectedRevision,
  };
};
